import { defineMessages } from 'react-intl';

type MessageKeys =
	| 'cardTitle'
	| 'collapseLabel'
	| 'expandLabel'
	| 'removeLabel'
	| 'archiveButton'
	| 'showMore'
	| 'showLess'
	| 'backLabel'
	| 'removeFromCleanUp'
	| 'moreInfoTooltip'
	| 'viewProfile'
	| 'archiveTeamButton'
	| 'archivedSuccess'
	| 'archiving'
	| 'couldNotArchive'
	| 'teamDescription'
	| 'teamDescriptionMembersOnly'
	| 'errorTooltip';

const message: Record<MessageKeys, { id: string; defaultMessage: string; description?: string }> =
	defineMessages({
		cardTitle: {
			id: 'atlassian-team-update-archive.card-title',
			defaultMessage: 'Clean up teams',
			description:
				'Title text displayed at the top of an action card in the AI conversation interface when the AI suggests cleaning up teams. Shown as the card header title.',
		},
		collapseLabel: {
			id: 'atlassian-team-update-archive.collapse-label',
			defaultMessage: 'Collapse',
			description:
				'Accessibility label for the chevron button that collapses the teams clean up card content.',
		},
		expandLabel: {
			id: 'atlassian-team-update-archive.expand-label',
			defaultMessage: 'Expand',
			description:
				'Accessibility label for the chevron button that expands the teams clean up card content.',
		},
		removeLabel: {
			id: 'atlassian-team-update-archive.remove-label',
			defaultMessage: 'Remove from clean up',
			description:
				'Accessibility label for the remove button next to each team in the clean up list.',
		},
		archiveButton: {
			id: 'atlassian-team-update-archive.archive-button',
			defaultMessage: '{teamCount, plural, one {Archive team} other {Archive {teamCount} teams}}',
			description:
				'Button label displayed on the primary action button in the teams clean up action card. The {teamCount} placeholder is replaced with the number of teams to archive.',
		},
		showMore: {
			id: 'atlassian-team-update-archive.show-more',
			defaultMessage: 'Show {count} more',
			description:
				'Button label to reveal additional teams in the clean up list. The {count} placeholder is replaced with the number of hidden teams.',
		},
		showLess: {
			id: 'atlassian-team-update-archive.show-less',
			defaultMessage: 'Show less',
			description:
				'Button label to collapse the teams list back to showing only the initial subset.',
		},
		backLabel: {
			id: 'atlassian-team-update-archive.back-label',
			defaultMessage: 'Back',
			description:
				'Accessibility label for the back button in the team detail view that returns to the team list.',
		},
		removeFromCleanUp: {
			id: 'atlassian-team-update-archive.remove-from-clean-up',
			defaultMessage: 'Remove from clean up',
			description: 'Button label to remove a team from the clean up list in the team detail view.',
		},
		moreInfoTooltip: {
			id: 'atlassian-team-update-archive.more-info-tooltip',
			defaultMessage: 'More info',
			description: 'Tooltip text shown when hovering over the more info button.',
		},
		viewProfile: {
			id: 'atlassian-team-update-archive.view-profile',
			defaultMessage: 'View profile',
			description: 'Button label to navigate to the team profile page from the team detail view.',
		},
		archiveTeamButton: {
			id: 'atlassian-team-update-archive.archive-team-button',
			defaultMessage: 'Archive team',
			description: 'Button label to archive a single team from the team detail view.',
		},
		archivedSuccess: {
			id: 'atlassian-team-update-archive.archived-success',
			defaultMessage: '{teamCount} {teamCount, plural, one {team} other {teams}} archived',
			description:
				'Success message shown in the card after teams have been archived. The {teamCount} placeholder is replaced with the number of archived teams.',
		},
		archiving: {
			id: 'atlassian-team-update-archive.archiving',
			defaultMessage: 'Archiving {teamCount} {teamCount, plural, one {team} other {teams}}',
			description:
				'Subtitle shown in the card while teams are being archived. The {teamCount} placeholder is replaced with the number of teams.',
		},
		couldNotArchive: {
			id: 'atlassian-team-update-archive.could-not-archive',
			defaultMessage:
				'Error archiving {teamCount} {teamCount, plural, one {team} other {teams}}. Try again later.',
			description:
				'Subtitle shown in the card when some teams failed to archive. The {teamCount} placeholder is replaced with the number of teams.',
		},
		teamDescription: {
			id: 'atlassian-team-update-archive.team-description',
			defaultMessage:
				'{teamType} \u00B7 {memberCount} {memberCount, plural, one {member} other {members}}',
			description:
				'Description line for a team in the clean up list, showing the team type and member count. E.g. "Team \u00B7 3 members".',
		},
		teamDescriptionMembersOnly: {
			id: 'atlassian-team-update-archive.team-description-members-only',
			defaultMessage: '{memberCount} {memberCount, plural, one {member} other {members}}',
			description: 'Description line when only member count is available.',
		},
		errorTooltip: {
			id: 'atlassian-team-update-archive.error-tooltip',
			defaultMessage: 'Archive failed',
			description: 'Tooltip text shown when a team fails to archive.',
		},
	});

export default message;
